import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Partner {
  id: number;
  name: string;
  logo: string;
  description: string;
}

const PartnersCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  const partners = [
    { id: 1, name: 'MedLife', logo: './Logo-Medlife.png', description: 'Cel mai mare furnizor de servicii medicale private din România, partener de încredere de aproape trei decenii.' },
    { id: 2, name: 'Gebrüder Weiss', logo: './Gebruder.png', description: 'Cea mai veche companie de transport și logistică din Austria, în proprietatea familiilor fondatoare.' },
    { id: 3, name: 'Clinica Sanovil', logo: './Sanovil.png', description: 'Spital privat acreditat ANMCS înființat în 2007, în continuu proces de extindere a serviciilor medicale.' },
    { id: 4, name: 'Pall-Ex Group', logo: './PALL-EX.png', description: 'Înființat în Marea Britanie în 1996, răspuns la nevoia clienților de reducere a stocurilor și creșterea flexibilității.' },
    { id: 5, name: 'Logistic E Van Wijk', logo: './E-van-wijk.png', description: 'Înființată la începutul anilor \'90 în Cluj-Napoca, parteneriat româno-olandez, furnizor de servicii logistice integrate.' },
    { id: 6, name: 'Quehenberger', logo: './Quehenberger.png', description: 'Companie holding în managementul strategic și operativ al furnizorilor de logistică orientați spre calitate.' },
  ];

  // Optimized responsive breakpoints
  const getItemsPerSlide = useCallback(() => {
    if (typeof window === 'undefined') return 3;
    
    const width = window.innerWidth;
    if (width < 640) return 1;      // Mobile
    if (width < 768) return 1;      // Large mobile
    if (width < 1024) return 2;     // Tablet
    if (width < 1280) return 3;     // Desktop
    return 3;                       // Large desktop
  }, []);

  const totalSlides = Math.ceil(partners.length / itemsPerSlide);

  // Handle resize with debouncing
  useEffect(() => {
    let resizeTimer;
    
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newItemsPerSlide = getItemsPerSlide();
        if (newItemsPerSlide !== itemsPerSlide) {
          setItemsPerSlide(newItemsPerSlide);
          setCurrentSlide(0); // Reset to first slide on resize
        }
      }, 100);
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [itemsPerSlide, getItemsPerSlide]);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && totalSlides > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, 5000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, totalSlides]);

  // Navigation functions
  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [totalSlides, isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [totalSlides, isTransitioning]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [currentSlide, isTransitioning]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
    
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  const getCurrentPartners = useCallback(() => {
    const start = currentSlide * itemsPerSlide;
    return partners.slice(start, start + itemsPerSlide);
  }, [currentSlide, itemsPerSlide]);

  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden overflow-x-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-64 h-64 md:w-96 md:h-96 bg-blue-200/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-0 w-64 h-64 md:w-96 md:h-96 bg-purple-200/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
              PARTENERII NOȘTRI
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-4 md:mb-6">
            Colaborăm cu <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">liderii</span> industriei
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Prin parteneriatele strategice cu cele mai importante companii de transport și logistică, 
            oferim servicii de cea mai înaltă calitate la nivel global.
          </p>
        </div>

        {/* Carousel Container */}
        <div 
          ref={containerRef}
          className="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Desktop Navigation Buttons */}
          {totalSlides > 1 && (
            <>
              <button
                onClick={prevSlide}
                disabled={isTransitioning}
                className="hidden md:flex absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-6 z-20 bg-white hover:bg-gray-50 text-gray-700 hover:text-blue-600 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed items-center justify-center"
                aria-label="Slide anterior"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={nextSlide}
                disabled={isTransitioning}
                className="hidden md:flex absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-6 z-20 bg-white hover:bg-gray-50 text-gray-700 hover:text-blue-600 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed items-center justify-center"
                aria-label="Slide următor"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Mobile Navigation */}
          {totalSlides > 1 && (
            <div className="md:hidden flex justify-between items-center mb-6">
              <button
                onClick={prevSlide}
                disabled={isTransitioning}
                className="flex items-center justify-center bg-white hover:bg-gray-50 text-gray-700 hover:text-blue-600 p-3 rounded-full shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] min-h-[44px]"
                aria-label="Slide anterior"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <div className="text-sm text-gray-600 font-medium bg-white px-3 py-1 rounded-full shadow-sm">
                {currentSlide + 1} / {totalSlides}
              </div>
              
              <button
                onClick={nextSlide}
                disabled={isTransitioning}
                className="flex items-center justify-center bg-white hover:bg-gray-50 text-gray-700 hover:text-blue-600 p-3 rounded-full shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] min-h-[44px]"
                aria-label="Slide următor"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Partners Grid */}
          <div className={`grid gap-4 sm:gap-6 transition-all duration-300 w-full ${
  itemsPerSlide === 1 ? 'grid-cols-1' :
  itemsPerSlide === 2 ? 'grid-cols-1 md:grid-cols-2' :
  'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
}`}>
            {getCurrentPartners().map((partner, index) => (
              <div
                key={`${partner.id}-${currentSlide}`}
                className="group bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-100/50 animate-fade-in"
                style={{
                  animationDelay: `${index * 150}ms`,
                  animationFillMode: 'both'
                }}
              >
                {/* Gradient Background on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Partner Logo Container */}
                <div className="relative z-10 mb-6">
                  <div className="w-full h-20 md:h-24 flex items-center justify-center bg-gray-50 rounded-xl group-hover:bg-gray-100 transition-all duration-300 overflow-hidden">
                    <img
                      src={partner.logo}
                      alt={`${partner.name} logo`}
                      className="max-w-full max-h-14 md:max-h-16 object-contain transition-all duration-300 transform group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        const img = e.target;
                        img.src = `data:image/svg+xml;base64,${btoa(`
                          <svg width="200" height="80" xmlns="http://www.w3.org/2000/svg">
                            <rect width="100%" height="100%" fill="#f9fafb"/>
                            <text x="50%" y="50%" font-family="system-ui,-apple-system,sans-serif" font-size="14" fill="#3b82f6" text-anchor="middle" dominant-baseline="middle" font-weight="600">
                              ${partner.name}
                            </text>
                          </svg>
                        `)}`;
                      }}
                    />
                  </div>
                </div>

                {/* Partner Info */}
                <div className="relative z-10 text-center">
                  <h3 className="font-bold text-lg md:text-xl text-gray-800 mb-3 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                    {partner.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">
                    {partner.description}
                  </p>
                </div>

                {/* Hover Effect Line */}
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-500"></div>
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          {totalSlides > 1 && (
            <div className="flex justify-center mt-8 md:mt-12 space-x-2">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  disabled={isTransitioning}
                  className={`h-2 md:h-3 rounded-full transition-all duration-300 disabled:cursor-not-allowed ${
                    currentSlide === index
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 w-8 md:w-10'
                      : 'bg-gray-300 hover:bg-gray-400 w-2 md:w-3'
                  }`}
                  aria-label={`Du-te la slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Mobile Swipe Indicator */}
          <div className="md:hidden text-center mt-4">
            <p className="text-xs text-gray-500 bg-white/50 rounded-full px-3 py-1 inline-block">
              Glisează pentru a vedea mai mulți parteneri
            </p>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { number: '6+', label: 'Parteneri Strategici', color: 'text-blue-600' },
            { number: '5+', label: 'Ani de Colaborare', color: 'text-purple-600' },
            { number: '100%', label: 'Încredere Reciprocă', color: 'text-green-600' }
          ].map((stat, index) => (
            <div 
              key={index} 
              className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100/50"
            >
              <div className={`text-3xl md:text-4xl font-bold ${stat.color} mb-2`}>
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium text-sm md:text-base">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Smooth transitions for all interactive elements */
        button, .group {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Enhanced hover effects */
        .group:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        /* Better focus states for accessibility */
        button:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Responsive improvements */
        @media (max-width: 640px) {
          .container {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in,
          .transition-all,
          .transform {
            animation: none !important;
            transition: none !important;
          }
        }

        /* Touch improvements for mobile */
        @media (hover: none) and (pointer: coarse) {
          .hover\\:shadow-xl:hover {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
          
          .hover\\:-translate-y-1:hover {
            transform: translateY(-2px);
          }
        }
      `}</style>
    </section>
  );
};

export default PartnersCarousel;